/*
This file is located in the main branch
Last time the file was updated (UTC): [05.03.2024 14:30:32]
Permissions to update outside the main branch: [false]
*/

import { AxiosError } from 'axios';
import { Trans } from 'react-i18next';
import { AxiosErrorResponse, UserErrorDetails } from '../models/api/axiosErrorResponse';

export function defaultFormatter(
    error?: AxiosError<AxiosErrorResponse>
): string | JSX.Element | null {
    const resp = error?.response;
    const errorData = resp?.data;

    if (resp?.status === 403) {
        return accessDeniedFormatter(error);
    }

    if (errorData && 'errorCode' in errorData) {
        if (errorData.errorCode === 'BackOfficeErrorValidation') {
            return validationErrorFormatter(error);
        }

        if (errorData.errorCode === 'BackOfficeErrorUserCreate') {
            return userErrorFormatter(error);
        }

        return <Trans i18nKey={errorData.errorCode} values={errorData.errorData} />;
    }

    return null;
}

function accessDeniedFormatter(
    error?: AxiosError<AxiosErrorResponse>
): string | JSX.Element | null {
    const errorData = error?.response?.data;

    return (
        <>
            <h5>
                <Trans i18nKey="BackOfficeAccessDenied" />
            </h5>
            {error?.config?.url && <p>URL: {error?.config?.url}</p>}
            {errorData && <p>{JSON.stringify(errorData)}</p>}
        </>
    );
}

type FormattedMessagePlaceholderValue = {
    ComparisonValue: any;
    ComparisonProperty: string;
    PropertyName: string;
    PropertyValue: any;
    CollectionIndex: number;
};

type ValidationError = {
    propertyName: string;
    errorMessage: string;
    errorCode: string;
    formattedMessagePlaceholderValues: FormattedMessagePlaceholderValue[];
};

function validationErrorFormatter(
    error?: AxiosError<AxiosErrorResponse<ValidationError[]>>
): string | JSX.Element | null {
    const errorData = error?.response?.data;
    const validationErrors = errorData?.errorData;
    return (
        <>
            <h5>
                <Trans i18nKey="BackOfficeErrorValidation" />
            </h5>
            <ul>
                {validationErrors &&
                    validationErrors.map((err, id) => (
                        <li key={id}>
                            {err.propertyName}:{' '}
                            <Trans
                                i18nKey={`BackOffice${err.errorCode}`}
                                values={err.formattedMessagePlaceholderValues}
                            />
                        </li>
                    ))}
            </ul>
        </>
    );
}

export type ChangePasswordErrorItem = {
    code: string | undefined;
    description: string | undefined;
};

export function changePasswordErrorFormatter(error?: any): string | JSX.Element | null {
    const errorData = (error as AxiosError<AxiosErrorResponse<ChangePasswordErrorItem[]>>)?.response
        ?.data;

    if (errorData) {
        if (errorData.errorCode === 'BackOfficeErrorChangePasswordFailed') {
            const items = errorData.errorData;
            return (
                <>
                    <h5>
                        <Trans i18nKey={errorData.errorCode} />
                    </h5>

                    <ul>
                        {items &&
                            items.map((item, ids) => (
                                <li key={ids}>{<Trans i18nKey={`BackOffice${item.code}`} />}</li>
                            ))}
                    </ul>
                </>
            );
        }
    }
    return defaultFormatter(error);
}

export function userErrorFormatter(error?: AxiosError<AxiosErrorResponse<UserErrorDetails[]>>) {
    const errorData = error?.response?.data;

    if (errorData) {
        const items = errorData.errorData;
        return (
            <>
                <h5>
                    <Trans i18nKey={errorData.errorCode} />
                </h5>

                <ul>
                    {items &&
                        items.map((item, ids) => (
                            <li key={ids}>
                                <Trans
                                    i18nKey={`BackOffice${item.code}`}
                                    defaults={item.description}
                                />
                            </li>
                        ))}
                </ul>
            </>
        );
    }

    return defaultFormatter(error);
}
